import React, { useMemo, useState } from 'react'
import {
  AppBar,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  useMediaQuery
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import {
  Menu as MenuIcon,
  Home as HomeIcon,
  ExitToApp,
  People,
  Star,
  ShoppingCart,
  Event,
  Payment,
  LocalOffer,
  Report as ReportIcon,
  BarChart,
  Inventory2,
  CardGiftcard,
  OnlinePrediction
} from '@mui/icons-material'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'

const drawerWidth = 300

const AdminDashboardLayout: React.FC = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [mobileOpen, setMobileOpen] = useState(false)

  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout } = useAuth()

  const menuItems = useMemo(
    () => [
      { text: 'Gerenciar Usuários', icon: <People />, path: '/admin/usuarios' },
      { text: 'Usuários VIP', icon: <Star />, path: '/admin/vip' },
      { text: 'Itens', icon: <Inventory2 />, path: '/admin/itens' },
      { text: 'Kits Iniciais', icon: <CardGiftcard />, path: '/admin/kits-iniciais' },
      { text: 'Players Online', icon: <OnlinePrediction />, path: '/admin/online' },
      { text: 'Gerenciar Pacotes', icon: <ShoppingCart />, path: '/admin/pacotes' },
      { text: 'Eventos', icon: <Event />, path: '/admin/eventos' },
      { text: 'Gerenciar Pagamentos', icon: <Payment />, path: '/admin/pagamentos' },
      { text: 'Gerenciar Cupons', icon: <LocalOffer />, path: '/admin/cupons' },
      { text: 'Gerenciar Denúncias', icon: <ReportIcon />, path: '/admin/denuncias' },
      { text: 'Estatísticas', icon: <BarChart />, path: '/admin/estatisticas' }
    ],
    []
  )

  const toggleDrawer = () => setMobileOpen((v) => !v)

  const goHome = () => {
    navigate('/')
    if (isMobile) setMobileOpen(false)
  }

  const doLogout = () => {
    logout()
    navigate('/')
    if (isMobile) setMobileOpen(false)
  }

  const go = (path: string) => {
    navigate(path)
    if (isMobile) setMobileOpen(false)
  }

  const drawer = (
    <Box sx={{ height: '100%', bgcolor: '#0f0f1c', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 3, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <Typography variant="h6" sx={{ fontWeight: 900 }}>
          Área Admin
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
          {user?.username}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<HomeIcon />}
            onClick={goHome}
            sx={{
              flex: 1,
              borderColor: 'rgba(255,255,255,0.14)',
              color: 'text.primary',
              '&:hover': { borderColor: 'primary.main', bgcolor: 'rgba(0,230,118,0.10)' }
            }}
          >
            Home
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ExitToApp />}
            onClick={doLogout}
            sx={{
              flex: 1,
              borderColor: 'rgba(255,255,255,0.14)',
              color: 'text.primary',
              '&:hover': { borderColor: 'secondary.main', bgcolor: 'rgba(255,109,0,0.10)' }
            }}
          >
            Sair
          </Button>
        </Box>
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <List sx={{ px: 2, py: 2 }}>
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path
            return (
              <ListItem key={item.path} disablePadding sx={{ mb: 1 }}>
                <ListItemButton
                  onClick={() => go(item.path)}
                  sx={{
                    borderRadius: 2,
                    py: 1.2,
                    bgcolor: isActive ? 'rgba(0,217,255,0.16)' : 'transparent',
                    border: isActive ? '1px solid rgba(0,217,255,0.30)' : '1px solid transparent',
                    '&:hover': { bgcolor: 'rgba(0,217,255,0.10)' }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: isActive ? 'info.main' : 'text.secondary' }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{ fontWeight: 800, color: 'text.primary' }}
                  />
                </ListItemButton>
              </ListItem>
            )
          })}
        </List>
      </Box>
    </Box>
  )

  return (
    <Box sx={{ display: 'flex', minHeight: '100dvh' }}>
      {isMobile && (
        <AppBar position="fixed" elevation={0}>
          <Toolbar>
            <IconButton color="inherit" edge="start" onClick={toggleDrawer} sx={{ mr: 1 }}>
              <MenuIcon />
            </IconButton>
            <Typography sx={{ fontWeight: 900 }}>Admin • Palworld</Typography>
          </Toolbar>
        </AppBar>
      )}

      <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
        <Drawer
          variant={isMobile ? 'temporary' : 'permanent'}
          open={isMobile ? mobileOpen : true}
          onClose={toggleDrawer}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              borderRight: '1px solid rgba(255,255,255,0.08)'
            }
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          pt: isMobile ? 9 : 0,
          minHeight: '100dvh'
        }}
      >
        <Outlet />
      </Box>
    </Box>
  )
}

export default AdminDashboardLayout
